import type { AulaEvento, ProgrammaEvento, SessioneEvento } from '@eventi/tipi.ts';
import { useNotifications } from '../../store/useStore';
import { cn } from '../../lib/cn';
import { eventiService } from '../../lib/eventi/eventiService';
import { messaggioErrore } from '../../lib/eventi/errori';
import { fuoriDaiTurni, incontriDaScegliere } from '../../lib/eventi/griglia';
import { nomeAula } from '../../lib/eventi/programma';
import { statoPartecipante } from '../../lib/eventi/stati';
import type { Evento, PartecipanteEvento } from '../../lib/eventi/tipi';
import { CampoPosti } from './CampoPosti';
import { GrigliaGiorno } from './GrigliaGiorno';
import { ImportaProgramma } from './ImportaProgramma';
import { Pannello } from './Pannello';
import { SceltaGiorno } from './SceltaGiorno';
import { useGiornoScelto } from './useGiornoScelto';

type Props = {
  evento: Evento;
  programma: ProgrammaEvento;
  partecipanti: PartecipanteEvento[];
  onEventoCambiato: () => Promise<void>;
};

function CellaProgramma({ sessione }: { sessione: SessioneEvento }) {
  const { sceglibile } = sessione;
  return (
    <div
      className={cn(
        'h-full rounded-lg border p-2.5',
        sceglibile ? 'border-gray-200 bg-white shadow-sm' : 'border-dashed border-gray-300 bg-gray-50',
      )}
    >
      <p className={cn('text-[13px] font-medium leading-snug line-clamp-3', sceglibile ? 'text-gray-900' : 'text-neutral-500')}>
        {sessione.titolo}
      </p>
      {sessione.relatori && (
        <p className="mt-1 text-[11px] text-neutral-500 line-clamp-1" title={sessione.relatori}>
          {sessione.relatori}
        </p>
      )}
      {!sceglibile && (
        <span className="mt-2 inline-block rounded-full bg-gray-200/70 px-2 py-0.5 text-[10px] font-medium text-neutral-600">
          non si sceglie
        </span>
      )}
    </div>
  );
}

function IntestazioneAula({ aula, incontri, onSalvaPosti }: {
  aula: AulaEvento;
  incontri: number;
  onSalvaPosti: (codiceAula: string, posti: number | null) => Promise<void>;
}) {
  return (
    <div className="flex h-full flex-col justify-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
      <span className="truncate text-sm font-semibold text-gray-900" title={aula.nome}>
        {aula.nome}
      </span>
      {incontri > 0 ? (
        <CampoPosti aula={aula} onSalva={onSalvaPosti} />
      ) : (
        <span className="text-[11px] text-neutral-400">niente da scegliere</span>
      )}
    </div>
  );
}

/** Le sessioni del giorno fuori dai turni (la plenaria del mattino): per tutti, non si scelgono. */
function FuoriDaiTurni({ programma, sessioni }: { programma: ProgrammaEvento; sessioni: SessioneEvento[] }) {
  if (sessioni.length === 0) return null;
  return (
    <div className="mb-5">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Fuori dai turni</p>
      <ul className="space-y-1.5">
        {sessioni.map((s) => (
          <li key={s.codice} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
            <span className="w-24 flex-shrink-0 text-xs font-semibold tabular-nums text-neutral-700">
              {s.inizio}–{s.fine}
            </span>
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium text-gray-900">{s.titolo}</span>
              <span className="text-neutral-500"> · {nomeAula(programma, s.aula)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * La scheda Programma: il file da importare e, sotto, il programma come
 * orario di un giorno alla volta, con i posti di ogni aula accanto al nome.
 */
export function SchedaProgramma({ evento, programma, partecipanti, onEventoCambiato }: Props) {
  const { addNotification } = useNotifications();
  const [giorno, setGiorno] = useGiornoScelto(programma.giorni);
  const conScelte = partecipanti.filter((p) => statoPartecipante(p) !== 'da_scegliere').length;

  const importa = async (nuovo: ProgrammaEvento) => {
    try {
      await eventiService.importaProgramma(evento.id, nuovo);
      addNotification({ type: 'success', title: 'Programma importato', message: `${nuovo.sessioni.length} sessioni.` });
      await onEventoCambiato();
    } catch (e) {
      addNotification({ type: 'error', title: 'Import non riuscito', message: messaggioErrore(e) });
      throw e;
    }
  };

  const salvaPosti = async (codiceAula: string, posti: number | null) => {
    try {
      await eventiService.aggiornaPosti(evento.id, codiceAula, posti);
      await onEventoCambiato();
    } catch (e) {
      addNotification({ type: 'error', title: 'Posti non salvati', message: messaggioErrore(e) });
      throw e;
    }
  };

  return (
    <div className="space-y-4 md:space-y-5">
      <ImportaProgramma programma={programma} importatoIl={evento.programma_importato_at} conScelte={conScelte} onImporta={importa} />

      {programma.giorni.length > 0 && (
        <Pannello
          titolo="Il programma"
          sotto="Accanto a ogni aula scrivi quanti posti ha: la proposta di assegnazione non li supera. Vuoto = senza limite. Si salva da solo quando esci dal campo."
          azioni={<SceltaGiorno giorni={programma.giorni} attivo={giorno} onCambia={setGiorno} />}
        >
          <FuoriDaiTurni programma={programma} sessioni={fuoriDaiTurni(programma, giorno)} />
          <GrigliaGiorno
            programma={programma}
            giorno={giorno}
            intestazioneAula={(aula) => (
              <IntestazioneAula aula={aula} incontri={incontriDaScegliere(programma, aula.codice)} onSalvaPosti={salvaPosti} />
            )}
            cella={(s) => <CellaProgramma sessione={s} />}
            vuoto="In questo giorno non ci sono incontri divisi in turni."
          />
          <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded border border-gray-200 bg-white shadow-sm" /> si sceglie nel percorso
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded border border-dashed border-gray-300 bg-gray-50" /> non si sceglie
            </span>
          </p>
        </Pannello>
      )}
    </div>
  );
}
